import { orpcContract } from "@jurni/contracts";
import { createORPCClient, onError, ORPCError } from "@orpc/client";
import type { ContractRouterClient } from "@orpc/contract";
import type { JsonifiedClient } from "@orpc/openapi-client";
import { OpenAPILink } from "@orpc/openapi-client/fetch";
import { getClientShopId } from "./client-shop";

export type {
  Product,
  ProductExtraData,
  ProductKeyBenefit,
  ProductKeyBenefits,
  ProductOffer,
} from "@jurni/schemas";

export type {
  ProductCustomerReview,
  ProductCustomerReviews,
  ProductFaqItem,
} from "@jurni/storefront-ui/schemas";

const CLIENT_SHOP_ID_HEADER = "x-client-shop-id";

const isClientShopMismatchError = (error: unknown) =>
  error instanceof ORPCError && error.code === "CLIENT_SHOP_MISMATCH";

const notifyClientShopMismatch = async () => {
  const { toast } = await import("sonner");
  toast.error("You switched to a different store in another tab", {
    id: "client-shop-mismatch",
    duration: Infinity,
    action: { label: "Reload", onClick: () => window.location.reload() },
  });
};

const link = new OpenAPILink(orpcContract, {
  url: `${process.env.JURNI_API_URL}/api/v2`,
  interceptors: [
    onError((error) => {
      if (typeof window !== "undefined" && isClientShopMismatchError(error)) {
        void notifyClientShopMismatch();
      }
    }),
  ],
  headers: async () => {
    if (typeof window !== "undefined") {
      const clientShopId = getClientShopId();
      return clientShopId
        ? { [CLIENT_SHOP_ID_HEADER]: String(clientShopId) }
        : {};
    }

    const { headers } = await import("next/headers");
    const headersList = await headers();
    return {
      cookie: headersList.get("cookie") ?? "",
    };
  },
  fetch: (request, init, context) => {
    const mergedInit: RequestInit = {
      ...init,
      ...context?.context,
      credentials: "include", // force credentials
    };
    const reqObj = new Request(request, mergedInit);
    return globalThis.fetch(reqObj);
  },
});

export const backendClient: JsonifiedClient<
  ContractRouterClient<typeof orpcContract, RequestInit>
> = createORPCClient(link);

// Export types
export type BackendAPIListAdminShopsResult = Awaited<
  ReturnType<typeof backendClient.admin.shops.list>
>;
export type BackendAPIAdminShopType =
  BackendAPIListAdminShopsResult["shops"][number];

export type BackendAPISwitchableShopType = Awaited<
  ReturnType<typeof backendClient.session.listSwitchableShops>
>["shops"][number];

export type BackendAPIListProductsResult = Awaited<
  ReturnType<typeof backendClient.products.list>
>;

export type BackendAPIProduct =
  BackendAPIListProductsResult["products"][number];

export type BackendAPIFunnelProductDataResult = Awaited<
  ReturnType<typeof backendClient.funnels.getProductData>
>;

export type BackendAPIFunnelProductData =
  BackendAPIFunnelProductDataResult["data"];

export type BackendAPIFunnelUpdateInput = Parameters<
  typeof backendClient.funnels.update
>[0];

export type BackendAPIMembersResult = Awaited<
  ReturnType<typeof backendClient.shop.listMembers>
>;

export type BackendAPIBrandTokens = Parameters<
  typeof backendClient.theme.updateBrandTokens
>[0];

export type BackendAPIBrandTokensResult = Awaited<
  ReturnType<typeof backendClient.theme.getBrandTokens>
>;

export type BackendAPITypography = Parameters<
  typeof backendClient.theme.updateTypography
>[0]["typography"];
